import { useState } from 'react';
import { ACSApi } from '../../../api';
import { useContainer } from '../../useContainer';
import { DisabledDays, DisabledDaysWithMessage } from '../../../commonTypes/acs';
import { LeaveRequestDto, MedicalLeaveRequestDto } from '../../../commonTypes/leaveRequest.types';
import { DateTime, Interval } from 'luxon';
import { useTranslation } from '../../useTranslation';

const useHttpGetDisabledDays = () => {
    const { t } = useTranslation('leaveRequest');
    const acsApi: ACSApi = useContainer(ACSApi);
    const [disabledDays, setDisabledDays] = useState<DisabledDaysWithMessage[]>([
        { message: t('youHaveTimeLog'), dates: [] },
        { message: t('youHaveLeaveRequest'), dates: [] },
    ]);
    const [leaveRequestDates, setLeaveRequestDates] = useState<DateTime[]>([]);

    const getDisabledDaysWithMessage = (from: string, to: string) => {
        const { exec, abort } = acsApi.getDisabledDays({
            query: { from: encodeURIComponent(from), to: encodeURIComponent(to) },
            onSuccess: res => {
                setDisabledDays(addMessageToDisabledDays(res.body, to));
                setLeaveRequestDates(extractDatesFromLeaveRequests(res.body));
            },
            onError: error => error,
        });

        exec();
        return abort;
    };

    const addMessageToDisabledDays = (disabledDays: DisabledDays & { medicalLeaves?: MedicalLeaveRequestDto[] }, to: string) => {
        const leaveRequests = disabledDays.leaveRequests || [];
        const medicalLeaves = disabledDays.medicalLeaves || [];

        const ddWithMessage: DisabledDaysWithMessage[] = [];

        ddWithMessage.push({ message: t('youHaveTimeLog'), dates: disabledDays.timeLogDates });

        if (leaveRequests.length > 0) {
            ddWithMessage.push({ message: t('youHaveLeaveRequest'), dates: leaveRequestToInterval(leaveRequests, to) });
        }

        if (medicalLeaves.length > 0) {
            ddWithMessage.push({
                message: 'This is your Medical Leave',
                dates: leaveRequestToInterval(medicalLeaves, to),
            });
        }

        return ddWithMessage;
    };

    const extractDatesFromLeaveRequests = (disabledDays: DisabledDays) => {
        const leaveRequests = disabledDays.leaveRequests || [];
        let allDates: string[] = [];

        leaveRequests.forEach(leaveRequest => {
            const fromDate = DateTime.fromISO((leaveRequest as any).fromDate || (leaveRequest as any).startDate);
            const toDate =
                (leaveRequest as any).toDate || (leaveRequest as any).endDate
                    ? DateTime.fromISO((leaveRequest as any).toDate || (leaveRequest as any).endDate).plus({ day: 1 })
                    : fromDate.plus({ day: 1 });

            const interval = Interval.fromDateTimes(fromDate, toDate);
            const dates = interval.splitBy({ day: 1 }).map(d => d.start.toISO());
            allDates = [...allDates, ...dates];
        });

        return allDates.map(date => DateTime.fromISO(date));
    };

    const leaveRequestToInterval = (leaveRequests: (LeaveRequestDto | MedicalLeaveRequestDto)[], to: string) => {
        let disabledLeaveRequestDays: string[] = [];

        const viewTo = DateTime.fromISO(to);

        leaveRequests.forEach(leaveRequest => {
            const fromDate = DateTime.fromISO((leaveRequest as any).fromDate || (leaveRequest as any).startDate);
            let toDate: DateTime;

            if ((leaveRequest as any).toDate || (leaveRequest as any).endDate) {
                toDate = DateTime.fromISO((leaveRequest as any).toDate || (leaveRequest as any).endDate).plus({ day: 1 });
            } else {
                toDate = viewTo > fromDate ? viewTo : fromDate.plus({ day: 1 });
            }

            const interval = Interval.fromDateTimes(fromDate, toDate);
            const disabledDays = interval.splitBy({ day: 1 }).map(d => d.start.toISO());
            disabledLeaveRequestDays = [...disabledLeaveRequestDays, ...disabledDays];
        });

        return disabledLeaveRequestDays;
    };

    return {
        disabledDays,
        getDisabledDaysWithMessage,
        leaveRequestDates,
    };
};

export default useHttpGetDisabledDays;
